import React from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * Map Legend Component
 *
 * Displays a compact total count badge at the top-right corner
 */

interface MapLegendProps {
  position?: L.ControlPosition;
  statistics?: {
    total: number;
    fromDatabase?: number;
    geocoded?: number;
    manufacturingUnits?: number;
  };
}

/**
 * Custom Control Component for Leaflet
 */
const LegendControl: React.FC<{
  position: L.ControlPosition;
  statistics?: MapLegendProps['statistics'];
}> = ({ position, statistics }) => {
  const map = useMap();

  React.useEffect(() => {
    const container = L.DomUtil.create('div', 'leaflet-control');
    container.style.background = 'white';
    container.style.padding = '8px 12px';
    container.style.borderRadius = '8px';
    container.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
    container.style.fontFamily = 'system-ui, -apple-system, sans-serif';

    // Compact HTML content - show both gaushalas and manufacturing units
    const hasManufacturing = statistics?.manufacturingUnits !== undefined && statistics.manufacturingUnits > 0;

    container.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 6px;">
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 16px;">🐄</span>
          <span style="font-size: 13px; font-weight: 600; color: #1f2937;">Gaushalas:</span>
          <span style="font-size: 14px; font-weight: 700; color: #10b981;">${statistics?.total || 0}</span>
        </div>
        ${hasManufacturing ? `
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 16px;">🏭</span>
          <span style="font-size: 13px; font-weight: 600; color: #1f2937;">Manufacturing:</span>
          <span style="font-size: 14px; font-weight: 700; color: #3b82f6;">${statistics?.manufacturingUnits || 0}</span>
        </div>
        ` : ''}
      </div>
    `;

    // Add control to map
    const control = L.control({ position });
    control.onAdd = () => container;
    control.addTo(map);

    return () => {
      control.remove();
    };
  }, [map, position, statistics]);

  return null;
};

const MapLegend: React.FC<MapLegendProps> = ({
  position = 'topright',
  statistics,
}) => {
  return <LegendControl position={position} statistics={statistics} />;
};

export default MapLegend;
